import { asOnceOnly } from '../store/middleware/RunOnceMiddleware';
import { IItemCacheDispatch, IItemCacheContext, IItemCacheThunkAction } from '../store/actions/ItemCacheActions';
import { ItemPreserver, IItemDemands } from '../models/itemCache/ItemPreserver';
import { createAsyncActionCreator, IAsyncActionCreator } from '../store/actions/createActionCreator';
import { pruneItems } from '../store/reducers/itemMapReducer';
import { IItemCacheState } from '../store/reducers/itemCacheReducer';
import { pruningConfigurationFacet } from '../facets/ConfigurationFacets';

const getItemPreserver = asOnceOnly((dispatch: IItemCacheDispatch, getState: () => IItemCacheState, context: IItemCacheContext) => {
  const {
    configuration
  } = context;

  const pruningConfiguration = pruningConfigurationFacet.unpack(configuration);

  const itemPreserver = new ItemPreserver(new Date().getTime(), {
    maxAge: pruningConfiguration && pruningConfiguration.maxAge,
    pageDuration: pruningConfiguration && pruningConfiguration.pageDuration
  });

  return itemPreserver;
});

/**
 * Payload for an action to prune the cache.
 * @public
 */
export interface IPruneLastAcessedItemsPayload {
  /**
   * Specifies the maximum age of recently-accessed items to preserve in the cache.
   * A value less than the configured `pageDuration`, such as `0`, will prune
   * all recently-accessed items which are not currently in-demand.
   * Larger values will preserve recently-accessed items within the time window.
   *
   * If not specified, the default value in the cache will be used.
   */
  maxAge?: number;
}

/**
 * Creates an action which, when dispatched, prunes the cache to contain only items
 * which are currently in-demand or have been recently accessed within a maximum age limit.
 *
 * Set `maxAge` to `0` to prune all items except those in-demand.
 * An unspecified `maxAge` will use the default value configured in the cache.
 * @public
 */
export const pruneLastAccessedItems: IAsyncActionCreator<IPruneLastAcessedItemsPayload, IItemCacheThunkAction<void>> =
  createAsyncActionCreator((payload: IPruneLastAcessedItemsPayload) => {
    return (dispatch: IItemCacheDispatch) => {
      const itemPreserver = dispatch(getItemPreserver);

      itemPreserver.trimLastAccesses(new Date().getTime(), payload.maxAge);

      const items = itemPreserver.getPreservedItems();

      dispatch(pruneItems({
        items: items
      }));
    };
  });

/**
 * @public
 */
export interface IRegisterItemDemandsPayload {
  /**
   * The object which owns the demands.
   */
  owner: object;
  /**
   * A set of items to preserve while they remain in-demand.
   */
  items: IItemDemands;
}

/**
 * Creates an action which, when dispatched, registers that a component has current demands
 * on specific items.
 *
 * Registering demands ensures that the items do not get prematurely pruned from the cache.
 * To remove demands registered via this action, use `removeItemDemands` with the same `owner` value.
 * @public
 */
export const registerItemDemands: IAsyncActionCreator<IRegisterItemDemandsPayload, IItemCacheThunkAction<void>> =
  createAsyncActionCreator((payload: IRegisterItemDemandsPayload) => {
    return (dispatch: IItemCacheDispatch) => {
      const itemPreserver = dispatch(getItemPreserver);

      itemPreserver.registerDemands(payload.owner, payload.items);
    };
  });

/**
 * Payload for an action to remove item demands belonging to an owner.
 * @public
 */
export interface IRemoveItemDemandsPayload {
  /**
   * The object which owns the demands.
   * Must be the same object as passed using `registerItemDemands`.
   */
  owner: object;
}

/**
 * Creates an action which, when dispatched, registers demands for items belonging to
 * a specific owner.
 * @public
 */
export const removeItemDemands: IAsyncActionCreator<IRemoveItemDemandsPayload, IItemCacheThunkAction<void>> =
  createAsyncActionCreator((payload: IRemoveItemDemandsPayload) => {
    return (dispatch: IItemCacheDispatch) => {
      const itemPreserver = dispatch(getItemPreserver);

      itemPreserver.removeDemands(payload.owner);
    };
  });

/**
 * Payload for an action to mark items as recently-accessed.
 * @public
 */
export interface IRecordLastItemAccessesPayload {
  items: IItemDemands;
}

/**
 * Creates an action which, when dispatched, records the specified set of items
 * as having been accessed.
 * @public
 */
export const recordLastItemAccesses: IAsyncActionCreator<IRecordLastItemAccessesPayload, IItemCacheThunkAction<void>> =
  createAsyncActionCreator((payload: IRecordLastItemAccessesPayload) => {
    return (dispatch: IItemCacheDispatch) => {
      const itemPreserver = dispatch(getItemPreserver);

      itemPreserver.recordLastAccesses(payload.items, new Date().getTime());
    };
  });

/**
 * Payload for an action to register permanent item demands.
 * @public
 */
export interface IRegisterPermanentDemandsPayload {
  items: IItemDemands;
}

/**
 * Creates an action which, when dispatched, permanently registers demands for specified items,
 * so that the items are never pruned.
 *
 * This should only be done for items which cannot be re-fetched, and should be limited to one-time
 * configuration data.
 * @public
 */
export const registerPermanentItemDemands: IAsyncActionCreator<IRegisterPermanentDemandsPayload, IItemCacheThunkAction<void>> =
  createAsyncActionCreator((payload: IRegisterPermanentDemandsPayload) => {
    return (dispatch: IItemCacheDispatch) => {
      dispatch(registerItemDemands({
        owner: {},
        items: payload.items
      }));
    };
  });
